﻿<script lang="ts" setup>
    import { onMounted, reactive } from 'vue';
    import BSFormInputText from '@shared/components/BSFormInputText.vue';
    import Modal from '@shared/components/Modal.vue';
    import SelectIssueFromList from '@shared/components/SelectIssueFromList.vue';
    import { loadIssuesForDoubleOptIn, loadIssuesForTransactional, FormIssue } from './FormEditor.access';
    import { getKeyValById } from './FormEditor';
    import { vPopoverInfo } from '@shared/utils/directives/popovers';
    import BSFormInputTel from '@shared/components/BSFormInputTel.vue';

    type SettingsProps = {
        confirmationIssueId: string | null;
        doubleOptInIssueId: string | null;
        doubleOptInUrl: string | null;
        subscriptionConfirmedUrl: string;
        subscriptionFailedUrl: string;
        defaultCountryCode: string;
    };

    const props = defineProps<SettingsProps>();

    const state = reactive<{
        doubleOptInIssues: FormIssue[];
        confirmationIssues: FormIssue[];
        showDoubleOptInIssues: boolean;
        showTransactionalIssues: boolean;
    }>({
        doubleOptInIssues: [],
        confirmationIssues: [],
        showDoubleOptInIssues: false,
        showTransactionalIssues: false,
    });

    onMounted(async () => {
        state.doubleOptInIssues = await loadIssuesForDoubleOptIn();
        state.confirmationIssues = await loadIssuesForTransactional();
    });
</script>

<template>
    <fieldset class="fieldset m-lg-t">
        <legend>{{ $Resources.SubsciptionFormsDoubleOptInHeading }}</legend>

        <div class="ug-form-group-2">
            <label>
                {{ $Resources.SubsciptionFormsDoubleOptInEmailLabel }}:
                <button
                    type="button"
                    class="popover-trigger"
                    v-popover-info.right="{
                        heading: $Resources.SubsciptionFormsDoubleOptInEmailLabel,
                        body: $Resources.SubsciptionFormsDoubleOptInEmailDescription,
                    }">
                    <span class="glyphicon glyphicon-info-sign" aria-hidden="true"></span>
                </button>
            </label>

            <div class="input-group">
                <span class="form-control" @click="state.showDoubleOptInIssues = true" :class="{ 'text-placeholder': !props.doubleOptInIssueId }">
                    {{
                        getKeyValById(props.doubleOptInIssueId, state.doubleOptInIssues, 'IssueId', 'Subject') ||
                        $Resources.SubsciptionFormsChooseDoubleOptInEmailPlaceholder
                    }}
                </span>
                <span class="input-group-btn">
                    <button class="btn btn-subtle" :disabled="!props.doubleOptInIssueId" role="button" @click="$emit('update:doubleOptInIssueId', null)">
                        <i class="glyphicon glyphicon-remove"></i>
                    </button>
                </span>
            </div>
            <div class="form-group m-sm-t">
                <button type="button" class="btn btn-default btn-sm" @click="state.showDoubleOptInIssues = true">
                    {{ $Resources.SubsciptionFormsChooseDoubleOptInEmailButton }}
                </button>
            </div>
        </div>

        <BSFormInputText
            class="form-group"
            :modelValue="props.doubleOptInUrl"
            @update:modelValue="(newValue: string) => $emit('update:doubleOptInUrl', newValue)"
            :label="$Resources.SubsciptionFormsDoubleOptInConfirmationUrlLabel"
            placeholder="https://"
            :infoText="$Resources.SubsciptionFormsDoubleOptInConfirmationUrlDescription"></BSFormInputText>
    </fieldset>

    <Modal :title="$Resources.SubsciptionFormsDoubleOptInHeading" :show="state.showDoubleOptInIssues" @close="state.showDoubleOptInIssues = false" size="lg">
        <template #content>
            <div class="alert alert-info alert-fixed-i">
                <p>
                    <i class="fa fa-info-circle" aria-hidden="true"></i>
                    <strong>{{ $Resources.IssueNeedsToBeSentAlertHeading }}</strong>
                    {{ $Resources.IssueNeedsToBeSentAlertText }}
                </p>
            </div>
            <SelectIssueFromList
                :items="state.doubleOptInIssues"
                type="issues"
                @update:selectedId="
                    (newVal: string) => {
                        $emit('update:doubleOptInIssueId', newVal);
                        state.showDoubleOptInIssues = false;
                    }
                "></SelectIssueFromList>
        </template>
    </Modal>

    <fieldset class="fieldset m-lg-t">
        <legend>{{ $Resources.SubsciptionFormsConfirmationEmailHeading }}</legend>

        <div class="ug-form-group-2">
            <label>
                {{ $Resources.SubsciptionFormsConfirmationEmailLabel }}:
                <button
                    type="button"
                    class="popover-trigger"
                    v-popover-info.right="{
                        heading: $Resources.SubsciptionFormsConfirmationEmailLabel,
                        body: $Resources.SubsciptionFormsConfirmationEmailDescription,
                    }">
                    <span class="glyphicon glyphicon-info-sign" aria-hidden="true"></span>
                </button>
            </label>

            <div class="input-group">
                <span class="form-control" @click="state.showTransactionalIssues = true" :class="{ 'text-placeholder': !props.confirmationIssueId }">
                    {{
                        getKeyValById(props.confirmationIssueId, state.confirmationIssues, 'IssueId', 'Subject') ||
                        $Resources.SubsciptionFormsChooseConfirmationEmailPlaceholder
                    }}
                </span>
                <span class="input-group-btn">
                    <button
                        class="btn btn-subtle"
                        :disabled="!props.confirmationIssueId"
                        role="button"
                        :title="$Resources.SubsciptionFormsRemoveDoubleOptInEmailButton"
                        @click="$emit('update:confirmationIssueId', null)">
                        <i class="glyphicon glyphicon-remove"></i>
                    </button>
                </span>
            </div>
            <div class="form-group m-sm-t">
                <button type="button" class="btn btn-default btn-sm" @click="state.showTransactionalIssues = true">
                    {{ $Resources.SubsciptionFormsChooseConfirmationEmailButton }}
                </button>
            </div>
        </div>
    </fieldset>

    <Modal :title="$Resources.ChooseIssueHeading" :show="state.showTransactionalIssues" @close="state.showTransactionalIssues = false" size="lg">
        <template #content>
            <div class="alert alert-info alert-fixed-i">
                <p>
                    <i class="fa fa-info-circle" aria-hidden="true"></i>
                    <strong>{{ $Resources.IssueNeedsToBeSentAlertHeading }}</strong>
                    {{ $Resources.IssueNeedsToBeSentAlertText }}
                </p>
            </div>
            <SelectIssueFromList
                :items="state.confirmationIssues"
                type="issues"
                @update:selectedId="
                    (newVal: string) => {
                        $emit('update:confirmationIssueId', newVal);
                        state.showTransactionalIssues = false;
                    }
                "></SelectIssueFromList>
        </template>
    </Modal>

    <h4>{{ $Resources.SubsciptionFormsResultUrlsHeading }}</h4>
    <BSFormInputText
        class="form-group"
        :modelValue="props.subscriptionConfirmedUrl"
        @update:modelValue="(newValue: string) => $emit('update:subscriptionConfirmedUrl', newValue)"
        :label="$Resources.SubsciptionFormsSuccessResultUrlLabel"
        placeholder="https://"
        :infoText="$Resources.SubsciptionFormsSuccessResultUrlDescription"></BSFormInputText>
    <BSFormInputText
        class="form-group"
        :modelValue="props.subscriptionFailedUrl"
        @update:modelValue="(newValue: string) => $emit('update:subscriptionFailedUrl', newValue)"
        :label="$Resources.SubsciptionFormsFailureResultUrlLabel"
        placeholder="https://"
        :infoText="$Resources.SubsciptionFormsFailureResultUrlDescription"></BSFormInputText>

    <h4>{{ $Resources.SubscribeFormGeneratorDefaultSmsCountryCodeLabel }}</h4>
    <BSFormInputTel
        class="form-group"
        :modelValue="props.defaultCountryCode"
        @update:modelValue="(newValue: string) => $emit('update:defaultCountryCode', newValue)"
        :label="$Resources.SubscribeFormGeneratorDefaultSmsCountryCodeLabel"
        :infoText="$Resources.ListImportSmsNumberHelpText.replace(/{DefaultCountryCode}/g, props.defaultCountryCode)"></BSFormInputTel>
</template>

<style></style>

<style scoped></style>
